import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setFeatureGate } from '../_redux/actions/featureGateActions'
import { useFeatureGateService } from '../_services/FeatureGateService'

// Track which flags are currently being fetched to prevent duplicate API calls across components
const fetchingFlags = new Set<string>()

/**
 * Generic hook to retrieve feature flag values with automatic caching and deduplication.
 * @param {string} flagName - Unique identifier for the feature flag (e.g., 'centrali3_0', 'maxFileSize')
 * @param {any} defaultValue - Fallback value if API call fails or flag doesn't exist (default: false)
 * @returns {Object} { value: flag value from Redux or defaultValue, isLoading: fetch status }
 */
export const useFeatureGate = (flagName: string, defaultValue: any = false) => {
  const dispatch = useDispatch()

  // Check Redux store for cached flag value (undefined = not yet fetched)
  const flagValueFromStore = useSelector(
    (state: any) => state.featureGateReducer?.flags?.[flagName]
  )

  // Service for making API calls to backend feature gate endpoints
  const { getFeatureGateValue } = useFeatureGateService()

  // Track loading state - true only when flag is undefined (not yet fetched)
  const [isLoading, setIsLoading] = useState(flagValueFromStore === undefined)

  useEffect(() => {
    const fetchFeatureGate = async () => {
      // Only fetch if value is not in Redux store (undefined means not fetched)
      // null, false, 0, "" are all valid cached values
      if (flagValueFromStore === undefined) {
        // Skip if another component is already fetching this flag (deduplication)
        if (fetchingFlags.has(flagName)) {
          return
        }

        // Mark flag as being fetched to prevent duplicate API calls
        fetchingFlags.add(flagName)
        setIsLoading(true)

        try {
          // Fetch flag value from backend API
          const value = await getFeatureGateValue(flagName)

          // Cache the fetched value in Redux for all components to use
          dispatch(setFeatureGate(flagName, value))
        } catch (error) {
          console.error(`Error fetching ${flagName} feature flag:`, error)

          // On error, store default value to prevent repeated failed API calls
          dispatch(setFeatureGate(flagName, defaultValue))
        } finally {
          // Clean up: remove flag from "currently fetching" set
          fetchingFlags.delete(flagName)
          setIsLoading(false)
        }
      }
    }

    fetchFeatureGate()
  }, [flagName, flagValueFromStore, getFeatureGateValue, dispatch, defaultValue])

  // Return cached value from Redux or fallback to defaultValue
  // Only use default when undefined (not fetched yet); null/false/0/'' are valid cached values
  return {
    value: flagValueFromStore !== undefined ? flagValueFromStore : defaultValue,
    isLoading
  }
}
